'use client';

import { useState, type FormEvent } from 'react';
import styles from './VendorSection.module.scss';

type VendorPackRequestFormProps = {
  lang?: string;
};

type Status = 'idle' | 'loading' | 'success' | 'error';

type Errors = {
  name?: string;
  email?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function VendorPackRequestForm({ lang }: VendorPackRequestFormProps) {
  const isEn = lang === 'en';

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — must stay empty
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');

  const labels = {
    name: isEn ? 'Name' : 'Nom',
    namePlaceholder: isEn ? 'Your name' : 'Votre nom',
    company: isEn ? 'Company' : 'Société',
    companyPlaceholder: isEn ? 'Your company' : 'Votre société',
    email: isEn ? 'Work email' : 'Email professionnel',
    emailPlaceholder: 'contact@societe.com',
    submit: isEn ? 'Request the Vendor Pack' : 'Demander le Vendor Pack',
    sending: isEn ? 'Sending…' : 'Envoi en cours…',
    retry: isEn ? 'Try again' : 'Réessayer',
    success: isEn ? 'Request sent.' : 'Demande envoyée.',
    error: isEn
      ? 'Something went wrong. Please try again.'
      : 'Une erreur est survenue. Veuillez réessayer.',
    nameRequired: isEn ? 'Name is required.' : 'Le nom est requis.',
    emailRequired: isEn ? 'Work email is required.' : "L'email professionnel est requis.",
    emailInvalid: isEn ? 'Invalid email address.' : 'Adresse email invalide.',
  };

  const validate = (): Errors => {
    const next: Errors = {};
    if (!name.trim()) {
      next.name = labels.nameRequired;
    }
    if (!email.trim()) {
      next.email = labels.emailRequired;
    } else if (!EMAIL_REGEX.test(email.trim())) {
      next.email = labels.emailInvalid;
    }
    return next;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setStatus('loading');
    try {
      const res = await fetch('/api/vendor-pack', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          company: company.trim(),
          email: email.trim(),
          lang: lang ?? 'fr',
          source: 'vendor-pack',
        }),
      });

      if (!res.ok) {
        throw new Error('Request failed');
      }
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <p className={`${styles.statusBanner} ${styles.statusSuccess}`} role="status">
        {labels.success}
      </p>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {/* Honeypot — hidden from humans, bots usually fill it */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        className={styles.honeypot}
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        aria-hidden="true"
      />

      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label htmlFor="vendor-pack-name" className={styles.label}>
            {labels.name}
          </label>
          <input
            id="vendor-pack-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            className={styles.input}
            placeholder={labels.namePlaceholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'vendor-pack-name-error' : undefined}
          />
          {errors.name && (
            <p id="vendor-pack-name-error" className={styles.fieldError} role="alert">
              {errors.name}
            </p>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor="vendor-pack-company" className={styles.label}>
            {labels.company}
          </label>
          <input
            id="vendor-pack-company"
            name="company"
            type="text"
            autoComplete="organization"
            className={styles.input}
            placeholder={labels.companyPlaceholder}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="vendor-pack-email" className={styles.label}>
            {labels.email}
          </label>
          <input
            id="vendor-pack-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className={styles.input}
            placeholder={labels.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'vendor-pack-email-error' : undefined}
          />
          {errors.email && (
            <p id="vendor-pack-email-error" className={styles.fieldError} role="alert">
              {errors.email}
            </p>
          )}
        </div>
      </div>

      {status === 'error' && (
        <p className={`${styles.statusBanner} ${styles.statusError}`} role="alert">
          {labels.error}
        </p>
      )}

      <div className={styles.submitRow}>
        <button type="submit" className={styles.submitButton} disabled={status === 'loading'}>
          {status === 'loading' ? labels.sending : labels.submit}
        </button>
      </div>
    </form>
  );
}
